import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, parseISO, getYear } from 'date-fns';
import { Plus, X, TrendingUp, TrendingDown, DollarSign, Users, FileText, Download, Mail } from 'lucide-react';

const COLORS = ['#7c6cf8', '#60a5fa', '#f5a623', '#ff4d6d', '#a78bfa', '#34d399', '#fb923c', '#38bdf8'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function MetricModal({ metric, onClose, year }) {
  const { addCeoMetric, updateCeoMetric, companies } = useApp();
  const isEdit = !!metric;
  const [form, setForm] = useState(metric || {
    year: year || new Date().getFullYear(),
    month: '',
    company: '',
    currency: 'UYU',
    premiumTotal: '',
    commissionRate: '',
    commissionTotal: '',
    policiesCount: '',
    clientsCount: '',
    notes: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePremiumChange = (val) => {
    set('premiumTotal', val);
    if (form.commissionRate) set('commissionTotal', Math.round(Number(val) * Number(form.commissionRate) / 100));
  };
  const handleRateChange = (val) => {
    set('commissionRate', val);
    if (form.premiumTotal) set('commissionTotal', Math.round(Number(form.premiumTotal) * Number(val) / 100));
  };

  const currSymbol = form.currency === 'USD' ? 'U$S' : '$';

  const submit = async (e) => {
    e.preventDefault();
    if (isEdit) await updateCeoMetric(metric.id, form);
    else await addCeoMetric(form);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>
            {isEdit ? 'Editar métrica' : 'Nueva métrica'}
          </h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Año *</label>
                <input type="number" value={form.year} onChange={e => set('year', e.target.value)} required min="2000" max="2099" />
              </div>
              <div className="form-group">
                <label className="form-label">Mes <span style={{ color:'var(--text-muted)', fontWeight:400, fontSize:11 }}>(vacío = anual)</span></label>
                <select value={form.month || ''} onChange={e => set('month', e.target.value)}>
                  <option value="">Registro anual</option>
                  {MESES.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Compañía</label>
                <select value={form.company} onChange={e => set('company', e.target.value)}>
                  <option value="">Cartera total</option>
                  {companies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Moneda</label>
                <select value={form.currency || 'UYU'} onChange={e => set('currency', e.target.value)}>
                  <option value="UYU">$ Pesos uruguayos</option>
                  <option value="USD">U$S Dólares</option>
                </select>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Prima total ({currSymbol})</label>
                <input type="number" value={form.premiumTotal} onChange={e => handlePremiumChange(e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">% Comisión</label>
                <input type="number" value={form.commissionRate} onChange={e => handleRateChange(e.target.value)} placeholder="0" step="0.1" />
              </div>
              <div className="form-group">
                <label className="form-label">Comisión ({currSymbol}) <span style={{ color:'var(--accent)', fontSize:11 }}>auto</span></label>
                <input type="number" value={form.commissionTotal} onChange={e => set('commissionTotal', e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">N° pólizas</label>
                <input type="number" value={form.policiesCount} onChange={e => set('policiesCount', e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">N° clientes</label>
                <input type="number" value={form.clientsCount} onChange={e => set('clientsCount', e.target.value)} placeholder="0" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notas</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Observaciones del período..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn btn-primary">{isEdit ? 'Guardar' : 'Agregar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Notificaciones por email ───────────────────────────────
// Matriz usuario × módulo: define quién recibe un mail cuando se crea algo
// en cada módulo (además del aviso directo al asignado, que siempre llega).
// Se guarda en el server (colección notificationSettings).
const NOTIF_MODULOS = [
  { id: 'claims',   label: 'Siniestros' },
  { id: 'tickets',  label: 'Tickets' },
  { id: 'debts',    label: 'Deudores' },
];

async function apiNotif(method, body) {
  const token = localStorage.getItem('nebb_token');
  const res = await fetch('/api/notification-settings', {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

function NotificacionesSection() {
  const { users, showToast } = useApp();
  const [conf, setConf] = useState(null); // null = cargando
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    apiNotif('GET').then(setConf).catch(() => setConf([]));
  }, []);

  const tiene = (userId, mod) =>
    ((conf || []).find(c => c.id === userId)?.modules || []).includes(mod);

  const toggle = (userId, mod) => {
    setConf(prev => {
      const lista = [...(prev || [])];
      const idx = lista.findIndex(c => c.id === userId);
      const mods = idx >= 0 ? [...(lista[idx].modules || [])] : [];
      const pos = mods.indexOf(mod);
      if (pos >= 0) mods.splice(pos, 1); else mods.push(mod);
      if (idx >= 0) lista[idx] = { ...lista[idx], modules: mods };
      else lista.push({ id: userId, modules: mods });
      return lista;
    });
  };

  const guardar = async () => {
    setSaving(true);
    try {
      await apiNotif('PUT', conf || []);
      showToast('Configuración de notificaciones guardada');
    } catch (e) {
      showToast('Error al guardar: ' + e.message, 'error');
    }
    setSaving(false);
  };

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mail size={16} color="var(--accent)" />
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700 }}>Notificaciones por email</h3>
        </div>
        <button className="btn btn-primary btn-sm" onClick={guardar} disabled={saving || conf === null}>
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
        Marcá qué módulos le avisan por mail a cada usuario cuando se carga algo nuevo.
        El asignado directo de un siniestro o ticket siempre recibe su aviso, esté marcado o no.
      </p>
      {conf === null ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Cargando…</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Usuario</th>
                {NOTIF_MODULOS.map(m => <th key={m.id} style={{ textAlign: 'center' }}>{m.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {(users || []).map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: u.email ? 'var(--text-muted)' : 'var(--amber)' }}>
                      {u.email || '⚠ Sin email — no puede recibir avisos'}
                    </div>
                  </td>
                  {NOTIF_MODULOS.map(m => (
                    <td key={m.id} style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={tiene(u.id, m.id)}
                        disabled={!u.email}
                        onChange={() => toggle(u.id, m.id)}
                        style={{ width: 16, height: 16, cursor: u.email ? 'pointer' : 'not-allowed' }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CeoPage() {
  const { policies, clients, companies, ceoMetrics, deleteCeoMetric } = useApp();
  const [modal, setModal] = useState(false);
  const [editMetric, setEditMetric] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // ── Datos en tiempo real del sistema ──────────────────────
  const activePolices = policies.filter(p => p.status === 'active' || p.status === 'endorsed');
  const totalPremiumLive = activePolices.reduce((s, p) => s + (Number(p.premium) || 0), 0);

  // Agrupar pólizas por compañía
  const byCompany = companies.map(co => {
    const pols = activePolices.filter(p => p.company === co.name);
    const premium = pols.reduce((s, p) => s + (Number(p.premium) || 0), 0);
    const commission = Math.round(premium * (co.commissionRate || 12) / 100);
    return { name: co.name, premium, commission, policies: pols.length, rate: co.commissionRate || 12 };
  }).filter(c => c.policies > 0).sort((a,b) => b.premium - a.premium);

  const totalCommissionLive = byCompany.reduce((s, c) => s + c.commission, 0);

  // Pólizas por ramo (para pie chart)
  const TYPE_LABELS = { auto:'Automóvil', moto:'Motocicleta', truck:'Transporte', fleet:'Flota', home:'Hogar', commercial:'Comercio', building:'Edificio', life:'Vida', accident:'Accidentes', health:'Salud', liability:'RC', other:'Otro' };
  const byType = Object.entries(
    activePolices.reduce((acc, p) => { const t = TYPE_LABELS[p.type] || p.type || 'Otro'; acc[t] = (acc[t] || 0) + 1; return acc; }, {})
  ).sort((a,b) => b[1]-a[1]).slice(0, 8).map(([name, value]) => ({ name, value }));

  // ── Datos históricos (métricas manuales) ──────────────────
  const [viewMode, setViewMode] = useState('annual'); // 'annual' | 'monthly'
  const [filterCurrency, setFilterCurrency] = useState('UYU');

  const years = [...new Set(ceoMetrics.map(m => Number(m.year)))].sort();

  // Gráfico ANUAL — una barra por año, solo métricas sin mes (anuales) o suma de mensuales
  const metricsForChart = years.map(y => {
    const ym = ceoMetrics.filter(m => Number(m.year) === y && !m.company && (m.currency || 'UYU') === filterCurrency);
    const annual = ym.filter(m => !m.month);
    const monthly = ym.filter(m => m.month);
    // Si hay registros anuales los usa, si no suma los mensuales
    const source = annual.length > 0 ? annual : monthly;
    return {
      year: y.toString(),
      prima: source.reduce((s,m) => s+(Number(m.premiumTotal)||0), 0),
      comision: source.reduce((s,m) => s+(Number(m.commissionTotal)||0), 0),
    };
  }).filter(d => d.prima > 0 || d.comision > 0);

  // Gráfico MENSUAL — 12 meses del año seleccionado
  const monthlyChartData = MESES.map((nombre, i) => {
    const m = ceoMetrics.filter(mm =>
      Number(mm.year) === selectedYear &&
      Number(mm.month) === i+1 &&
      !mm.company &&
      (mm.currency || 'UYU') === filterCurrency
    );
    return {
      mes: nombre.slice(0,3),
      prima: m.reduce((s,x) => s+(Number(x.premiumTotal)||0), 0),
      comision: m.reduce((s,x) => s+(Number(x.commissionTotal)||0), 0),
    };
  });

  // Comparativa YoY — solo métricas de la moneda seleccionada
  const currentYearMetrics = ceoMetrics.filter(m => Number(m.year) === selectedYear && (m.currency || 'UYU') === filterCurrency);
  const prevYearMetrics = ceoMetrics.filter(m => Number(m.year) === selectedYear - 1 && (m.currency || 'UYU') === filterCurrency);
  const currPremium = currentYearMetrics.reduce((s,m) => s + (Number(m.premiumTotal)||0), 0);
  const prevPremium = prevYearMetrics.reduce((s,m) => s + (Number(m.premiumTotal)||0), 0);
  const currCommission = currentYearMetrics.reduce((s,m) => s + (Number(m.commissionTotal)||0), 0);
  const prevCommission = prevYearMetrics.reduce((s,m) => s + (Number(m.commissionTotal)||0), 0);
  const premiumChange = prevPremium ? Math.round((currPremium - prevPremium) / prevPremium * 100) : null;
  const commissionChange = prevCommission ? Math.round((currCommission - prevCommission) / prevCommission * 100) : null;
  const currSymbolFilter = filterCurrency === 'USD' ? 'U$S' : '$';

  const exportCSV = () => {
    const rows = ceoMetrics.map(m => [m.year, m.month ? MESES[m.month-1] : 'Anual', m.company || 'Total', m.currency || 'UYU', m.premiumTotal, m.commissionRate, m.commissionTotal, m.policiesCount, m.clientsCount, m.notes].join(','));
    const csv = '\ufeff' + ['Año,Mes,Compañía,Moneda,Prima Total,% Comisión,Comisión Total,N° Pólizas,N° Clientes,Notas', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `NEBB_CEO_comisiones_${format(new Date(),'yyyy-MM-dd')}.csv`; a.click();
  };

  const tooltipStyle = { background: '#0f1629', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#f0f4ff' };

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">CEO — Panel ejecutivo</h1>
          <p className="page-subtitle">Análisis de cartera y comisiones</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={exportCSV}><Download size={14} /> Exportar</button>
          <button className="btn btn-primary" onClick={() => { setEditMetric(null); setModal(true); }}><Plus size={15} /> Agregar métrica</button>
        </div>
      </div>

      {/* ── KPIs en tiempo real ── */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
          📊 Cartera actual (tiempo real)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Prima total activa', value: '$' + totalPremiumLive.toLocaleString('es-UY'), icon: DollarSign, color: 'var(--accent)' },
            { label: 'Comisión estimada', value: '$' + totalCommissionLive.toLocaleString('es-UY'), icon: TrendingUp, color: '#60a5fa' },
            { label: 'Pólizas activas', value: activePolices.length, icon: FileText, color: 'var(--amber)' },
            { label: 'Clientes', value: clients.length, icon: Users, color: '#a78bfa' },
            { label: 'Compañías', value: byCompany.length, icon: DollarSign, color: '#34d399' },
          ].map(k => (
            <div key={k.label} className="card" style={{ borderLeft: `3px solid ${k.color}`, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: k.color, fontFamily: 'var(--font-display)', fontWeight: 600 }}>{k.label}</div>
              <div style={{ fontSize: 22, fontFamily: 'var(--font-display)', fontWeight: 700, color: k.color, marginTop: 4 }}>{k.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Gráficos actuales ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
        {/* Prima por compañía */}
        <div className="card">
          <div style={{ fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 16 }}>Prima por compañía</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byCompany.slice(0,8)}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill: '#8892b0', fontSize: 11 }} />
              <YAxis tick={{ fill: '#8892b0', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} formatter={v => ['$' + Number(v).toLocaleString('es-UY')]} />
              <Bar dataKey="prima" name="Prima" fill="var(--accent)" radius={[4,4,0,0]}>
                {byCompany.slice(0,8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pólizas por ramo */}
        <div className="card">
          <div style={{ fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 16 }}>Distribución por ramo</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={byType} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                {byType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Tabla por compañía ── */}
      <div className="card" style={{ marginBottom: 28, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14 }}>Comisión estimada por compañía</div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Compañía</th><th>Pólizas</th><th>Prima total</th><th>% Comisión</th><th>Comisión estimada</th><th>% de cartera</th></tr></thead>
            <tbody>
              {byCompany.map(c => (
                <tr key={c.name}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  <td style={{ fontSize: 13 }}>{c.policies}</td>
                  <td style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>${c.premium.toLocaleString('es-UY')}</td>
                  <td><span className="badge badge-blue">{c.rate}%</span></td>
                  <td style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)' }}>${c.commission.toLocaleString('es-UY')}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 80, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                        <div style={{ width: `${totalPremiumLive ? Math.round(c.premium / totalPremiumLive * 100) : 0}%`, height: '100%', background: 'var(--accent)', borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{totalPremiumLive ? Math.round(c.premium / totalPremiumLive * 100) : 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              <tr style={{ background: 'var(--accent-dim)', fontWeight: 700 }}>
                <td><strong>TOTAL</strong></td>
                <td>{activePolices.length}</td>
                <td style={{ fontFamily: 'var(--font-display)' }}><strong>${totalPremiumLive.toLocaleString('es-UY')}</strong></td>
                <td>—</td>
                <td style={{ fontFamily: 'var(--font-display)', color: 'var(--accent)' }}><strong>${totalCommissionLive.toLocaleString('es-UY')}</strong></td>
                <td>100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Histórico manual ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div style={{ fontSize:14, fontFamily:'var(--font-display)', fontWeight:700 }}>📅 Histórico de comisiones</div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          {/* Moneda */}
          <div style={{ display:'flex', background:'var(--bg-input)', borderRadius:8, overflow:'hidden', border:'1px solid var(--border)' }}>
            {[['UYU','$ Pesos'],['USD','U$S']].map(([v,l])=>(
              <button key={v} onClick={()=>setFilterCurrency(v)} style={{ padding:'6px 14px', fontSize:12, fontWeight:filterCurrency===v?700:400, background:filterCurrency===v?'var(--accent)':'transparent', color:filterCurrency===v?'#fff':'var(--text-secondary)', border:'none', cursor:'pointer' }}>{l}</button>
            ))}
          </div>
          {/* Vista anual/mensual */}
          <div style={{ display:'flex', background:'var(--bg-input)', borderRadius:8, overflow:'hidden', border:'1px solid var(--border)' }}>
            {[['annual','Anual'],['monthly','Mensual']].map(([v,l])=>(
              <button key={v} onClick={()=>setViewMode(v)} style={{ padding:'6px 14px', fontSize:12, fontWeight:viewMode===v?700:400, background:viewMode===v?'var(--accent-dim)':'transparent', color:viewMode===v?'var(--accent)':'var(--text-secondary)', border:'none', cursor:'pointer' }}>{l}</button>
            ))}
          </div>
          {/* Año */}
          <select value={selectedYear} onChange={e=>setSelectedYear(Number(e.target.value))} style={{ width:100 }}>
            {[...Array(6)].map((_,i)=>{ const y=new Date().getFullYear()-i; return <option key={y} value={y}>{y}</option>; })}
          </select>
        </div>
      </div>

      {/* Comparativa YoY */}
      {(currPremium>0||prevPremium>0) && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
          <div className="card" style={{ borderLeft:`3px solid ${premiumChange>=0?'var(--accent)':'var(--red)'}` }}>
            <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'var(--font-display)', fontWeight:600 }}>Prima {selectedYear} vs {selectedYear-1} ({filterCurrency})</div>
            <div style={{ fontSize:22, fontFamily:'var(--font-display)', fontWeight:700, color:premiumChange>=0?'var(--accent)':'var(--red)', marginTop:4 }}>
              {currSymbolFilter}{currPremium.toLocaleString('es-UY')}
            </div>
            {premiumChange!==null&&<div style={{ display:'flex', alignItems:'center', gap:4, fontSize:13, color:premiumChange>=0?'var(--accent)':'var(--red)', marginTop:4 }}>{premiumChange>=0?<TrendingUp size={14} />:<TrendingDown size={14} />}{premiumChange>0?'+':''}{premiumChange}% vs año anterior</div>}
          </div>
          <div className="card" style={{ borderLeft:`3px solid ${commissionChange>=0?'#60a5fa':'var(--red)'}` }}>
            <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'var(--font-display)', fontWeight:600 }}>Comisión {selectedYear} vs {selectedYear-1} ({filterCurrency})</div>
            <div style={{ fontSize:22, fontFamily:'var(--font-display)', fontWeight:700, color:commissionChange>=0?'#60a5fa':'var(--red)', marginTop:4 }}>
              {currSymbolFilter}{currCommission.toLocaleString('es-UY')}
            </div>
            {commissionChange!==null&&<div style={{ display:'flex', alignItems:'center', gap:4, fontSize:13, color:commissionChange>=0?'#60a5fa':'var(--red)', marginTop:4 }}>{commissionChange>=0?<TrendingUp size={14} />:<TrendingDown size={14} />}{commissionChange>0?'+':''}{commissionChange}% vs año anterior</div>}
          </div>
        </div>
      )}

      {/* Gráfico ANUAL */}
      {viewMode==='annual' && metricsForChart.length>0 && (
        <div className="card" style={{ marginBottom:20 }}>
          <div style={{ fontSize:13, fontFamily:'var(--font-display)', fontWeight:700, marginBottom:16 }}>
            Evolución anual — {filterCurrency==='USD'?'U$S':'Pesos'}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={metricsForChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="year" tick={{ fill:'#8892b0', fontSize:12 }} />
              <YAxis tick={{ fill:'#8892b0', fontSize:12 }} />
              <Tooltip contentStyle={tooltipStyle} formatter={v=>[currSymbolFilter+Number(v).toLocaleString('es-UY')]} />
              <Legend />
              <Line type="monotone" dataKey="prima" name="Prima total" stroke="var(--accent)" strokeWidth={2} dot={{ fill:'var(--accent)', r:4 }} />
              <Line type="monotone" dataKey="comision" name="Comisión" stroke="#60a5fa" strokeWidth={2} dot={{ fill:'#60a5fa', r:4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Gráfico MENSUAL */}
      {viewMode==='monthly' && (
        <div className="card" style={{ marginBottom:20 }}>
          <div style={{ fontSize:13, fontFamily:'var(--font-display)', fontWeight:700, marginBottom:16 }}>
            Evolución mensual {selectedYear} — {filterCurrency==='USD'?'U$S':'Pesos'}
          </div>
          {monthlyChartData.some(d=>d.prima>0||d.comision>0) ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="mes" tick={{ fill:'#8892b0', fontSize:11 }} />
                <YAxis tick={{ fill:'#8892b0', fontSize:11 }} />
                <Tooltip contentStyle={tooltipStyle} formatter={v=>[currSymbolFilter+Number(v).toLocaleString('es-UY')]} />
                <Legend />
                <Bar dataKey="prima" name="Prima" fill="var(--accent)" radius={[4,4,0,0]} />
                <Bar dataKey="comision" name="Comisión" fill="#60a5fa" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign:'center', padding:'32px 20px', color:'var(--text-muted)', fontSize:13 }}>
              No hay registros mensuales para {selectedYear} en {filterCurrency}.<br/>
              <button className="btn btn-primary btn-sm" style={{ marginTop:12 }} onClick={()=>{setEditMetric(null);setModal(true);}}>
                + Agregar mes
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tabla de métricas históricas */}
      {ceoMetrics.length>0 ? (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', fontFamily:'var(--font-display)', fontWeight:700, fontSize:14 }}>Registros históricos</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Año</th><th>Mes</th><th>Moneda</th><th>Compañía</th><th>Prima total</th><th>% Com.</th><th>Comisión</th><th>Pólizas</th><th>Notas</th><th></th></tr></thead>
              <tbody>
                {[...ceoMetrics].sort((a,b)=>{ const y=Number(b.year)-Number(a.year); if(y!==0) return y; return (Number(b.month)||0)-(Number(a.month)||0); }).map(m=>{
                  const sym = (m.currency||'UYU')==='USD'?'U$S':'$';
                  return (
                    <tr key={m.id}>
                      <td style={{ fontWeight:700, color:'var(--accent)' }}>{m.year}</td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{m.month ? MESES[m.month-1] : <span style={{ color:'var(--text-muted)', fontSize:11 }}>Anual</span>}</td>
                      <td><span className={`badge ${(m.currency||'UYU')==='USD'?'badge-blue':'badge-green'}`} style={{ fontSize:10 }}>{m.currency||'UYU'}</span></td>
                      <td style={{ fontSize:13 }}>{m.company||<span style={{ color:'var(--text-muted)' }}>Total</span>}</td>
                      <td style={{ fontFamily:'var(--font-display)', fontWeight:600 }}>{m.premiumTotal?sym+Number(m.premiumTotal).toLocaleString('es-UY'):'—'}</td>
                      <td>{m.commissionRate?`${m.commissionRate}%`:'—'}</td>
                      <td style={{ fontFamily:'var(--font-display)', fontWeight:700, color:'var(--accent)' }}>{m.commissionTotal?sym+Number(m.commissionTotal).toLocaleString('es-UY'):'—'}</td>
                      <td>{m.policiesCount||'—'}</td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)', maxWidth:140 }}>{m.notes}</td>
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-secondary btn-sm" onClick={()=>{setEditMetric(m);setModal(true);}}>Editar</button>
                          <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('¿Eliminar?'))deleteCeoMetric(m.id);}}>×</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card" style={{ textAlign:'center', padding:48, color:'var(--text-muted)' }}>
          <DollarSign size={32} style={{ marginBottom:12, opacity:0.3 }} />
          <p>Aún no hay datos históricos</p>
          <p style={{ fontSize:13, marginTop:6 }}>Agregá métricas anuales o mensuales para ver la evolución de la cartera</p>
          <button className="btn btn-primary" style={{ marginTop:16 }} onClick={()=>setModal(true)}>Agregar primer registro</button>
        </div>
      )}

      <NotificacionesSection />


      {modal && <MetricModal metric={editMetric} year={selectedYear} onClose={()=>{setModal(false);setEditMetric(null);}} />}
    </div>
  );
}